import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Input, Button, message, Tag, Popconfirm, Alert } from 'antd';
import { CheckCircleOutlined, DisconnectOutlined, GoogleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';
import { apiErrorMessage } from '../../utils/apiError';

const SYNC_STATUS = {
  running:   { metin: 'Çalışıyor',  renk: 'text-gray-600' },
  completed: { metin: 'Tamamlandı', renk: 'text-green-600' },
  failed:    { metin: 'Başarısız',  renk: 'text-red-500' },
  skipped:   { metin: 'Atlandı — yedek Drive\'a gitmedi', renk: 'text-orange-600' },
};

const OAUTH_MESSAGES = {
  connected:        { type: 'success', text: 'Google hesabı başarıyla bağlandı.' },
  denied:           { type: 'warning', text: 'İzin verilmedi, bağlantı tamamlanamadı.' },
  invalid_state:    { type: 'error', text: 'Güvenlik doğrulaması başarısız (state uyuşmadı), tekrar deneyin.' },
  missing_config:   { type: 'error', text: 'Client ID/Secret eksik görünüyor, tekrar kaydedip deneyin.' },
  no_refresh_token: { type: 'error', text: 'Google refresh token döndürmedi, tekrar deneyin.' },
  // Google onay ekranında izinler TEK TEK işaretleniyor. Drive kutusu
  // işaretlenmezse bağlantı "başarılı" görünür ama her yedekleme
  // 403 ile patlar; bu yüzden backend token'ı kaydetmiyor.
  missing_drive_scope: {
    type: 'error',
    text: 'Google Drive izni verilmedi. Onay ekranındaki TÜM kutuları — özellikle '
        + 'Drive dosyalarını görüntüleme/yönetme iznini — işaretleyip tekrar deneyin.',
  },
  error:            { type: 'error', text: 'Bağlantı sırasında bir hata oluştu, tekrar deneyin.' },
};

const GoogleDriveTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingClient, setSavingClient] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [savingFolder, setSavingFolder] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [folderId, setFolderId] = useState('');
  const [notes, setNotes] = useState('');

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/google-drive/settings');
      setSettings(res.data);
      setClientId(res.data.client_id || '');
      setFolderId(res.data.root_folder_id || '');
      setNotes(res.data.notes || '');
    } catch {
      message.error('Google Drive ayarları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  // OAuth callback'ten donusteki durum mesaji (?google_oauth=connected vb.)
  useEffect(() => {
    const status = searchParams.get('google_oauth');
    if (!status) return;
    const info = OAUTH_MESSAGES[status];
    if (info) message[info.type](info.text);
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSaveClient = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      message.warning('Client ID ve Client Secret gerekli.');
      return;
    }
    setSavingClient(true);
    try {
      await api.post('/google-drive/oauth/client', { client_id: clientId.trim(), client_secret: clientSecret.trim() });
      message.success('Client ID/Secret kaydedildi.');
      setClientSecret('');
      fetchSettings();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSavingClient(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await api.get('/google-drive/oauth/authorize');
      window.location.href = res.data.url;
    } catch (error) {
      message.error(apiErrorMessage(error, 'Bağlantı başlatılamadı.'));
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.delete('/google-drive/credentials');
      message.success('Bağlantı kaldırıldı.');
      fetchSettings();
    } catch {
      message.error('Bağlantı kaldırılamadı.');
    }
  };

  const handleSaveFolder = async () => {
    setSavingFolder(true);
    try {
      await api.patch('/google-drive/settings', { root_folder_id: folderId });
      message.success('Hedef klasör kaydedildi.');
      fetchSettings();
    } catch (error) {
      message.error(apiErrorMessage(error, 'Kaydedilemedi.'));
    } finally {
      setSavingFolder(false);
    }
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch('/google-drive/settings', { notes });
      message.success('Not kaydedildi.');
    } catch {
      message.error('Not kaydedilemedi.');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return null;

  return (
    <div className="space-y-4">
      <Alert
        type="info"
        showIcon
        message="Nasıl çalışır?"
        description={
          <div>
            <p className="mb-1">
              Her gece 03:00'te sertifikalar, yüklenen dosyalar (medya, iş takip ekleri vb.) ve veritabanı
              yedeği Google Drive'a senkronize edilir — yerelde silinen/güncellenen bir dosya Drive'da da
              silinir/güncellenir. Manuel tetiklemek için Ayarlar {'>'} Sistem Komutları {'>'} "Google Drive Yedekleme".
            </p>
            <p className="mb-0 text-gray-500">
              Servis hesabı yerine <strong>kendi Google hesabınızı</strong> OAuth ile bağlıyorsunuz — Google,
              servis hesaplarına depolama kotası tanımadığı için (kişisel hesaplarda bu "storageQuotaExceeded"
              hatasına yol açıyordu) yüklemeler artık bağladığınız hesabın kendi kotasına gidiyor.
            </p>
          </div>
        }
      />

      <Card title="1. Google OAuth Client">
        <p className="text-gray-500 text-sm mb-3">
          Google Cloud Console'da oluşturduğunuz OAuth 2.0 Client ID/Secret'i girin.
        </p>
        <div className="flex flex-col gap-2" style={{ maxWidth: 400 }}>
          <Input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="Client ID"
          />
          <Input.Password
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={settings?.has_oauth_client ? 'Client Secret (değiştirmek için girin)' : 'Client Secret'}
          />
          <Button type="primary" onClick={handleSaveClient} loading={savingClient} style={{ alignSelf: 'flex-start' }}>
            Kaydet
          </Button>
        </div>
      </Card>

      <Card title="2. Google Hesabı Bağlantısı">
        {settings?.is_connected ? (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Tag icon={<CheckCircleOutlined />} color="green">
              Bağlı — {settings.connected_email}
            </Tag>
            <Popconfirm
              title="Bağlantıyı kaldırmak istediğinize emin misiniz?"
              onConfirm={handleDisconnect}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button danger size="small" icon={<DisconnectOutlined />}>Bağlantıyı Kaldır</Button>
            </Popconfirm>
          </div>
        ) : (
          <div>
            <p className="text-gray-500 text-sm mb-3">
              {settings?.has_oauth_client
                ? 'Yukarıdaki Client ID/Secret kaydedildi. Şimdi kendi Google hesabınızla bağlanın.'
                : 'Önce yukarıdan Client ID/Secret kaydedin.'}
            </p>
            <Button
              type="primary"
              icon={<GoogleOutlined />}
              onClick={handleConnect}
              loading={connecting}
              disabled={!settings?.has_oauth_client}
            >
              Google ile Bağlan
            </Button>
          </div>
        )}
      </Card>

      <Card title="Hedef Klasör">
        <p className="text-gray-500 text-sm mb-3">
          Bağladığınız Google hesabına ait herhangi bir klasörün ID'sini girin (klasörün Drive linkindeki
          son parça, ör. .../folders/<strong>1a2B3cD4...</strong>) — kendi hesabınız olduğu için paylaşım
          yapmanıza gerek yok.
        </p>
        <div className="flex gap-2">
          <Input
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
            placeholder="Klasör ID'si"
            style={{ maxWidth: 400 }}
          />
          <Button type="primary" onClick={handleSaveFolder} loading={savingFolder}>Kaydet</Button>
        </div>
      </Card>

      {settings?.last_sync && (
        <Card title="Son Senkron Durumu" size="small">
          {/* Durum metni ile rengi TEK yerden türetiliyor. Eskiden ikisi ayrı
              koşuldu ve 'skipped' hem yeşil hem "Tamamlandı" görünüyordu —
              tam da bu ekranda fark edilmesi gereken arıza. */}
          <span className={SYNC_STATUS[settings.last_sync.status]?.renk ?? 'text-green-600'}>
            {SYNC_STATUS[settings.last_sync.status]?.metin ?? 'Tamamlandı'}
          </span>
          <span className="text-gray-400 ml-2 text-sm">
            {dayjs(settings.last_sync.started_at).format('DD.MM.YYYY HH:mm')}
          </span>
        </Card>
      )}

      <Card title="Not / Yapılacaklar">
        <Input.TextArea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Bu kuruluma dair unutulmaması gereken bir not varsa buraya yazın..."
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={5000}
        />
        <div className="flex justify-end mt-2">
          <Button onClick={handleSaveNotes} loading={savingNotes}>Notu Kaydet</Button>
        </div>
      </Card>
    </div>
  );
};

export default GoogleDriveTab;
